import React, { useState, useEffect } from "react";
import {
  format,
  addDays,
  addMonths,
  isSameDay,
  parseISO,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
} from "date-fns";
import { Link } from "react-router-dom";
import { FaHome } from "react-icons/fa";
import { 
  FaCalendar,
  FaCalculator,
  FaChartLine,
  FaFlask,
  FaSeedling,
  FaExclamationTriangle,
  FaCircle
} from "react-icons/fa";
import { Helmet } from "react-helmet-async";

const CalendarDay = ({
  date,
  isSelected,
  isPeriod,
  isActualPeriod,
  isFertile,
  isOvulation,
  onClick,
}) => {
  let dayClasses =
    "w-8 h-8 flex items-center justify-center rounded-full text-sm font-medium transition-all";

  // Base styling
  if (isSelected) {
    dayClasses += " ring-2 ring-purple-600";
  }

  // Background colors
  if (isActualPeriod) {
    dayClasses += " bg-pink-600 text-white";
  } else if (isPeriod) {
    dayClasses += " bg-pink-200";
  } else if (isFertile) {
    dayClasses += " bg-green-100";
  } else {
    dayClasses += " hover:bg-gray-100";
  }

  // Text styling
  if (isOvulation) {
    dayClasses += " font-bold";
  }

  // Current day styling
  const today = new Date();
  if (
    format(date, "yyyy-MM-dd") === format(today, "yyyy-MM-dd") &&
    !isActualPeriod &&
    !isPeriod
  ) {
    dayClasses += " border border-purple-400";
  }

  return (
    <div className={dayClasses} onClick={onClick}>
      {date.getDate()}
      {isOvulation && (
        <span className="absolute -top-1 -right-1 text-xs text-purple-600">
          ★
        </span>
      )}
    </div>
  );
};

const CalendarMonth = ({
  monthDate,
  periodDays,
  fertileWindow,
  ovulationDate,
  actualPeriodDays,
  onDateClick,
  selectedDate,
}) => {
  const startDate = startOfMonth(monthDate);
  const endDate = endOfMonth(monthDate);
  const dates = eachDayOfInterval({ start: startDate, end: endDate });

  // Get days from previous month to fill the first week
  const startDay = startDate.getDay();
  const prevMonthDays = [];
  if (startDay > 0) {
    const prevMonthEnd = addDays(startDate, -1);
    for (let i = 0; i < startDay; i++) {
      prevMonthDays.push(addDays(prevMonthEnd, -i));
    }
    prevMonthDays.reverse();
  }

  // Get days from next month to fill the last week
  const endDay = endDate.getDay();
  const nextMonthDays = [];
  if (endDay < 6) {
    for (let i = 1; i <= 6 - endDay; i++) {
      nextMonthDays.push(addDays(endDate, i));
    }
  }

  return (
    <div className="bg-white rounded-xl shadow-md p-4">
      <h3 className="text-center text-lg font-semibold text-purple-800 mb-3">
        {format(monthDate, "MMMM yyyy")}
      </h3>

      <div className="grid grid-cols-7 gap-1">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
          <div
            key={day}
            className="text-center text-xs font-medium text-purple-600 pb-1"
          >
            {day[0]}
          </div>
        ))}

        {prevMonthDays.map((date) => (
          <div
            key={`prev-${date}`}
            className="h-8 text-gray-400 text-sm flex items-center justify-center"
          >
            {date.getDate()}
          </div>
        ))}

        {dates.map((date) => {
          const dateStr = format(date, "yyyy-MM-dd");
          const isActualPeriod = actualPeriodDays.some(
            (d) => format(d, "yyyy-MM-dd") === dateStr
          );
          const isPeriod = periodDays.some(
            (d) => format(d, "yyyy-MM-dd") === dateStr
          );
          const isFertile = fertileWindow.some(
            (d) => format(d, "yyyy-MM-dd") === dateStr
          );
          const isOvulation = format(ovulationDate, "yyyy-MM-dd") === dateStr;
          const isSelected =
            selectedDate && format(selectedDate, "yyyy-MM-dd") === dateStr;

          return (
            <div key={dateStr} className="relative flex justify-center py-1">
              <CalendarDay
                date={date}
                isSelected={isSelected}
                isPeriod={isPeriod}
                isActualPeriod={isActualPeriod}
                isFertile={isFertile}
                isOvulation={isOvulation}
                onClick={() => onDateClick(date)}
              />
            </div>
          );
        })}

        {nextMonthDays.map((date) => (
          <div
            key={`next-${date}`}
            className="h-8 text-gray-400 text-sm flex items-center justify-center"
          >
            {date.getDate()}
          </div>
        ))}
      </div>
    </div>
  );
};

const MenstrualTracker = () => {
  const [startDate, setStartDate] = useState("");
  const [cycleLength, setCycleLength] = useState(28);
  const [periodLength, setPeriodLength] = useState(5);
  const [info, setInfo] = useState(null);
  const [actualPeriodDays, setActualPeriodDays] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);

  // Load saved data from localStorage
  useEffect(() => {
    const savedData = localStorage.getItem("menstrualTrackerData");
    if (savedData) {
      const parsedData = JSON.parse(savedData);
      setStartDate(parsedData.startDate || "");
      setCycleLength(parsedData.cycleLength || 28);
      setPeriodLength(parsedData.periodLength || 5);
      setActualPeriodDays(
        parsedData.actualPeriodDays?.map((d) => new Date(d)) || []
      );

      if (parsedData.startDate) {
        calculatePredictions(new Date(parsedData.startDate));
      }
    }
  }, []);

  // Save data to localStorage when it changes
  useEffect(() => {
    const dataToSave = {
      startDate,
      cycleLength,
      periodLength,
      actualPeriodDays,
    };
    localStorage.setItem("menstrualTrackerData", JSON.stringify(dataToSave));
  }, [startDate, cycleLength, periodLength, actualPeriodDays]);

  const calculatePredictions = (lastPeriodDate) => {
    const nextPeriodStart = addDays(lastPeriodDate, cycleLength);
    const periodDays = Array.from({ length: periodLength }, (_, i) =>
      addDays(nextPeriodStart, i)
    );
    const ovulationDate = addDays(nextPeriodStart, -14);
    const fertileWindow = Array.from({ length: 6 }, (_, i) =>
      addDays(ovulationDate, i - 5)
    );

    setInfo({
      nextPeriodStart,
      periodDays,
      fertileWindow,
      ovulationDate,
    });
  };

  const handleCalculate = () => {
    if (!startDate) return;
    calculatePredictions(new Date(startDate));
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
  };

  const getCurrentMonthDate = (offset = 0) => {
    const now = new Date();
    return addMonths(new Date(now.getFullYear(), now.getMonth(), 1), offset);
  };

  return (
    <div className="w-full font-nunito bg-gradient-to-br from-pink-50 to-purple-50 py-8">
   <Helmet>
    {/* Google Analytics */}
    <script
      async
      src="https://www.googletagmanager.com/gtag/js?id=G-YR8YMW9ERY"
    ></script>
    <script>
      {`
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', 'G-YR8YMW9ERY', {
          page_path: window.location.pathname,
        });
      `}
    </script>
    <title>Pregnancy Probability Tracker - FlowHearts | Ovulation & Fertility Calendar</title>
    <meta
      name="description"
      content="Track your menstrual cycle, ovulation, and fertile windows with FlowHearts' Pregnancy Probability Tracker. Plan conception with accurate period predictions."
    />

    {/* Canonical URL */}
    <link rel="canonical" href="https://flowhearts.com/pregnancy-probability" />

    {/* Open Graph / Facebook */}
    <meta property="og:type" content="website" />
    <meta property="og:url" content="https://flowhearts.com/pregnancy-probability" />
    <meta
      property="og:title"
      content="Pregnancy Probability Tracker - FlowHearts | Ovulation & Fertility Calendar"
    />
    <meta
      property="og:description"
      content="Monitor menstrual cycles, predict ovulation, and identify fertile windows with FlowHearts' Pregnancy Probability Tracker for effective conception planning."
    />
    <meta
      property="og:image"
      content="https://flowhearts.com/images/pregnancy-probability-preview.jpg"
    />

    {/* Twitter */}
    <meta property="twitter:card" content="summary_large_image" />
    <meta property="twitter:url" content="https://flowhearts.com/pregnancy-probability" />
    <meta
      property="twitter:title"
      content="Pregnancy Probability Tracker - FlowHearts | Ovulation & Fertility Calendar"
    />
    <meta
      property="twitter:description"
      content="Use FlowHearts' Pregnancy Probability Tracker to monitor cycles, ovulation, and fertility for precise conception planning and women's health insights."
    />
    <meta
      property="twitter:image"
      content="https://flowhearts.com/images/pregnancy-probability-preview.jpg"
    />

    {/* Structured Data */}
    <script type="application/ld+json">
      {`
        {
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "FlowHearts Pregnancy Probability Tracker",
          "url": "https://flowhearts.com/pregnancy-probability",
          "description": "Track menstrual cycles, ovulation, and fertile windows with FlowHearts' Pregnancy Probability Tracker for conception planning and health monitoring.",
          "applicationCategory": "HealthApplication",
          "operatingSystem": "Web",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          },
          "creator": {
            "@type": "Organization",
            "name": "FlowHearts"
          }
        }
      `}
    </script>
  </Helmet>
      <div className="max-w-8xl mx-auto px-4 md:px-6">
        {/* Back to Home Button */}
        <div className="flex justify-start mb-6">
          <Link
            to="/"
            className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-md hover:bg-pink-50 transition-colors duration-200"
          >
            <FaHome className="text-pink-500" />
            <span className="text-purple-700 font-medium">Back to Home</span>
          </Link>
        </div>

        {/* Header */}
        <header className="text-center mb-8">
          <h2 className="text-4xl font-cormorant font-bold text-purple-800 mb-2">
            Pregnancy Probability Checker
          </h2>
          <p className="text-pink-600">
            Track your menstrual cycle and fertility window
          </p>
        </header>

        {/* Input Section */}
        <div className="bg-gradient-to-r from-pink-100 to-purple-100 p-6 rounded-2xl shadow-md mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-purple-700 mb-1">
                First day of last period
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full p-3 rounded-lg bg-white border border-pink-200 focus:ring-2 focus:ring-purple-300 focus:border-purple-400"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-purple-700 mb-1">
                Cycle length (days)
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="20"
                  max="45"
                  value={cycleLength}
                  onChange={(e) => setCycleLength(parseInt(e.target.value))}
                  className="w-full p-3 rounded-lg bg-white border border-pink-200 focus:ring-2 focus:ring-purple-300 focus:border-purple-400"
                />
                <span className="absolute right-3 top-3 text-gray-400">
                  days
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-purple-700 mb-1">
                Period length (days)
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="2"
                  max="10"
                  value={periodLength}
                  onChange={(e) => setPeriodLength(parseInt(e.target.value))}
                  className="w-full p-3 rounded-lg bg-white border border-pink-200 focus:ring-2 focus:ring-purple-300 focus:border-purple-400"
                />
                <span className="absolute right-3 top-3 text-gray-400">
                  days
                </span>
              </div>
            </div>
          </div>

          <button
            onClick={handleCalculate}
            disabled={!startDate}
            className="w-full md:w-auto px-6 py-3 bg-gradient-to-r from-pink-500 to-purple-500 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Calculate My Cycle
          </button>
        </div>

        {info && (
          <>
            {/* Predictions Summary */}
            <div className="bg-white p-6 rounded-2xl shadow-md mb-8">
              <h2 className="text-xl font-bold text-purple-800 mb-4">
                Your Cycle Predictions
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-pink-50 p-4 rounded-xl border border-pink-100">
                  <h3 className="text-sm font-medium text-pink-700 mb-1">
                    Next Period
                  </h3>
                  <p className="text-lg font-semibold text-purple-800">
                    {format(info.nextPeriodStart, "MMM do")} -{" "}
                    {format(
                      addDays(info.nextPeriodStart, periodLength - 1),
                      "MMM do"
                    )}
                  </p>
                </div>

                <div className="bg-green-50 p-4 rounded-xl border border-green-100">
                  <h3 className="text-sm font-medium text-green-700 mb-1">
                    Fertile Window
                  </h3>
                  <p className="text-lg font-semibold text-purple-800">
                    {format(info.fertileWindow[0], "MMM do")} -{" "}
                    {format(info.fertileWindow[5], "MMM do")}
                  </p>
                </div>

                <div className="bg-purple-50 p-4 rounded-xl border border-purple-100">
                  <h3 className="text-sm font-medium text-purple-700 mb-1">
                    Ovulation Day
                  </h3>
                  <p className="text-lg font-semibold text-purple-800">
                    {format(info.ovulationDate, "MMM do")}
                  </p>
                </div>
              </div>
            </div>

            {/* Calendar Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              {/* Current Month Calendar */}
              <CalendarMonth
                monthDate={getCurrentMonthDate(0)}
                periodDays={info.periodDays}
                fertileWindow={info.fertileWindow}
                ovulationDate={info.ovulationDate}
                actualPeriodDays={actualPeriodDays}
                onDateClick={handleDateClick}
                selectedDate={selectedDate}
              />

              {/* Next Month Calendar */}
              <CalendarMonth
                monthDate={getCurrentMonthDate(1)}
                periodDays={info.periodDays}
                fertileWindow={info.fertileWindow}
                ovulationDate={info.ovulationDate}
                actualPeriodDays={actualPeriodDays}
                onDateClick={handleDateClick}
                selectedDate={selectedDate}
              />
            </div>

            {/* Legend */}
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
              <h3 className="text-sm font-semibold text-purple-800 mb-3">
                CALENDAR LEGEND
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-pink-600"></div>
                  <span className="text-sm">Actual period</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-pink-200"></div>
                  <span className="text-sm">Predicted period</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-green-100"></div>
                  <span className="text-sm">Fertile window</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-purple-600">★</span>
                  <span className="text-sm">Ovulation day</span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      {/* Educational Section */}
      <div className="max-w-8xl mx-auto px-4 md:px-6 mt-12">
        <div className="bg-white rounded-2xl shadow-md p-6 md:p-8">
          {/* How to Use Section */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-purple-800 mb-6 flex items-center">
              <FaHome className="mr-3 text-pink-500" />
              How to Use the Pregnancy Probability Checker
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-pink-50 rounded-xl p-6 border border-pink-200">
                <div className="w-12 h-12 bg-pink-500 text-white rounded-full flex items-center justify-center text-xl mb-4 mx-auto">
                  <FaCalendar className="text-lg" />
                </div>
                <h3 className="text-lg font-semibold text-center text-pink-700 mb-2">
                  Enter Your Data
                </h3>
                <p className="text-gray-700 text-center">
                  Input the first day of your last period, your typical cycle
                  length, and period duration.
                </p>
              </div>

              <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
                <div className="w-12 h-12 bg-purple-500 text-white rounded-full flex items-center justify-center text-xl mb-4 mx-auto">
                  <FaCalculator className="text-lg" />
                </div>
                <h3 className="text-lg font-semibold text-center text-purple-700 mb-2">
                  Get Predictions
                </h3>
                <p className="text-gray-700 text-center">
                  The calculator will show your fertile window, ovulation day,
                  and next period dates.
                </p>
              </div>

              <div className="bg-pink-50 rounded-xl p-6 border border-pink-200">
                <div className="w-12 h-12 bg-pink-500 text-white rounded-full flex items-center justify-center text-xl mb-4 mx-auto">
                  <FaChartLine className="text-lg" />
                </div>
                <h3 className="text-lg font-semibold text-center text-pink-700 mb-2">
                  Track Over Time
                </h3>
                <p className="text-gray-700 text-center">
                  Mark actual period days to improve future predictions and
                  track cycle patterns.
                </p>
              </div>
            </div>
          </div>

          {/* The Science Behind */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-purple-800 mb-6 flex items-center">
              <FaFlask className="mr-3 text-purple-500" />
              The Science Behind Fertility Tracking
            </h2>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200 mb-6">
              <h3 className="text-xl font-semibold text-purple-800 mb-4">
                Cycle Calculations
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="bg-white p-4 rounded-lg">
                  <p className="font-semibold text-pink-600 mb-2">
                    Next Period Date
                  </p>
                  <code className="block bg-gray-50 p-2 rounded text-purple-700 font-mono">
                    Next Period = Last Start + Cycle Length
                  </code>
                </div>

                <div className="bg-white p-4 rounded-lg">
                  <p className="font-semibold text-purple-600 mb-2">
                    Fertile Window
                  </p>
                  <code className="block bg-gray-50 p-2 rounded text-purple-700 font-mono">
                    Ovulation = Next Period - 14 days
                    <br />
                    Fertile Days = Ovulation ± 3 days
                  </code>
                </div>
              </div>

              <p className="text-gray-700">
                This method is based on the{" "}
                <span className="font-semibold">
                  standard 28-day cycle model
                </span>{" "}
                where ovulation typically occurs 14 days before the next period.
                The fertile window accounts for sperm survival (up to 5 days)
                and egg viability (12-24 hours).
              </p>
              <p className="text-gray-700 mt-4">
                Modern fertility tracking tools like this one help in ovulation prediction, making it easier to align with natural conception methods or family planning strategies.
              </p>
            </div>
          </div>

          {/* Biological Basis */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-purple-800 mb-6 flex items-center">
              <FaSeedling className="mr-3 text-green-500" />
              Understanding Your Fertility
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-green-50 rounded-xl p-6 border border-green-200">
                <h3 className="text-lg font-semibold text-green-700 mb-3">
                  Peak Fertility
                </h3>
                <p className="text-gray-700 mb-3">
                  Highest pregnancy probability occurs:
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <FaCircle className="text-green-500 mr-2 mt-1 text-xs" />
                    <span>1-2 days before ovulation</span>
                  </li>
                  <li className="flex items-start">
                    <FaCircle className="text-green-500 mr-2 mt-1 text-xs" />
                    <span>On ovulation day</span>
                  </li>
                </ul>
              </div>

              <div className="bg-pink-50 rounded-xl p-6 border border-pink-200">
                <h3 className="text-lg font-semibold text-pink-700 mb-3">
                  Cycle Phases
                </h3>
                <div className="space-y-3">
                  <div>
                    <p className="font-medium text-gray-800">
                      Follicular Phase
                    </p>
                    <p className="text-sm text-gray-700">
                      Days 1-14: Egg develops and estrogen rises
                    </p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Luteal Phase</p>
                    <p className="text-sm text-gray-700">
                      Days 15-28: Progesterone prepares uterus
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Accuracy Note */}
          <div className="bg-white rounded-xl p-6 shadow-inner border border-pink-300 mb-12">
            <h3 className="text-xl font-semibold text-pink-800 mb-4 flex items-center">
              <FaExclamationTriangle className="mr-2 text-pink-500" />
              Important Note on Accuracy
            </h3>
            <p className="text-gray-700 mb-3">
              While this calculator provides useful estimates, many factors can
              affect cycle regularity and fertility:
            </p>
            <ul className="space-y-2 text-gray-700 mb-4">
              <li className="flex items-start">
                <FaCircle className="text-pink-500 mr-2 mt-1 text-xs" />
                <span>Only 30% of women ovulate exactly on day 14</span>
              </li>
              <li className="flex items-start">
                <FaCircle className="text-pink-500 mr-2 mt-1 text-xs" />
                <span>Stress can shift ovulation by several days</span>
              </li>
              <li className="flex items-start">
                <FaCircle className="text-pink-500 mr-2 mt-1 text-xs" />
                <span>Medical conditions may affect cycle patterns</span>
              </li>
            </ul>
            <p className="text-gray-700">
              For conception purposes, combine this calendar method with
              ovulation tests and basal body temperature tracking for best
              results. Integrating these into your routine can enhance reproductive wellness and provide deeper insights into your body's natural rhythms.
            </p>
          </div>

          {/* New Section: Why Track Fertility? */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-purple-800 mb-6 flex items-center">
              <FaSeedling className="mr-3 text-pink-500" />
              Why Track Your Fertility?
            </h2>
            <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-xl p-6 border border-pink-200">
              <p className="text-gray-700 mb-4">
                In an era where women's health empowerment is on the rise, fertility tracking goes beyond basic period prediction. It offers a window into your overall well-being and can support various life goals.
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <FaCircle className="text-purple-500 mr-2 mt-1 text-xs flex-shrink-0" />
                  <span><strong>Conception Planning:</strong> Identify optimal times for trying to conceive, aligning with natural fertility awareness methods that are gaining popularity.</span>
                </li>
                <li className="flex items-start">
                  <FaCircle className="text-purple-500 mr-2 mt-1 text-xs flex-shrink-0" />
                  <span><strong>Health Insights:</strong> Detect irregularities that might signal hormonal imbalances or conditions like PCOS, contributing to proactive menstrual health management.</span>
                </li>
                <li className="flex items-start">
                  <FaCircle className="text-purple-500 mr-2 mt-1 text-xs flex-shrink-0" />
                  <span><strong>Lifestyle Optimization:</strong> Sync your energy levels, workouts, and nutrition with cycle phases for enhanced daily performance and self-care.</span>
                </li>
                <li className="flex items-start">
                  <FaCircle className="text-purple-500 mr-2 mt-1 text-xs flex-shrink-0" />
                  <span><strong>Empowerment Through Knowledge:</strong> Gain confidence in understanding your body, joining the growing community focused on reproductive health education.</span>
                </li>
              </ul>
              <p className="text-gray-700 mt-4">
                With tools like this pregnancy probability checker, you're taking a step towards informed decisions in your fertility journey.
              </p>
            </div>
          </div>

          {/* New Section: Frequently Asked Questions */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-purple-800 mb-6 flex items-center">
              <FaExclamationTriangle className="mr-3 text-purple-500" />
              Frequently Asked Questions
            </h2>
            <div className="space-y-6">
              <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
                <h3 className="text-lg font-semibold text-purple-700 mb-2">
                  How accurate is ovulation prediction?
                </h3>
                <p className="text-gray-700">
                  While based on standard models, actual ovulation can vary. Combine with physical signs like cervical mucus changes for better accuracy.
                </p>
              </div>
              <div className="bg-pink-50 rounded-xl p-6 border border-pink-200">
                <h3 className="text-lg font-semibold text-pink-700 mb-2">
                  Can I use this for birth control?
                </h3>
                <p className="text-gray-700">
                  This is for awareness only. For contraception, consult professionals and use reliable methods, as cycle variations can occur.
                </p>
              </div>
              <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
                <h3 className="text-lg font-semibold text-purple-700 mb-2">
                  What if my cycles are irregular?
                </h3>
                <p className="text-gray-700">
                  Track multiple cycles to average lengths. If highly variable, seek medical advice to understand underlying factors.
                </p>
              </div>
              <div className="bg-pink-50 rounded-xl p-6 border border-pink-200">
                <h3 className="text-lg font-semibold text-pink-700 mb-2">
                  How does age affect fertility windows?
                </h3>
                <p className="text-gray-700">
                  Fertility peaks in the 20s and declines after 35. This tool helps monitor changes, but personalized advice from experts is recommended.
                </p>
              </div>
            </div>
          </div>

          {/* New Section: Tips for Enhancing Fertility Awareness */}
          <div>
            <h2 className="text-3xl font-bold font-cormorant text-purple-800 mb-6 flex items-center">
              <FaChartLine className="mr-3 text-pink-500" />
              Tips for Enhancing Fertility Awareness
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-pink-50 rounded-xl p-6 border border-pink-200">
                <h3 className="text-lg font-semibold text-pink-700 mb-2">
                  Monitor Physical Signs
                </h3>
                <p className="text-gray-700">
                  Watch for changes in basal body temperature and cervical position to confirm ovulation predictions.
                </p>
              </div>
              <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
                <h3 className="text-lg font-semibold text-purple-700 mb-2">
                  Maintain a Healthy Lifestyle
                </h3>
                <p className="text-gray-700">
                  Balanced diet, regular exercise, and stress management can regulate cycles and improve overall reproductive health.
                </p>
              </div>
              <div className="bg-pink-50 rounded-xl p-6 border border-pink-200">
                <h3 className="text-lg font-semibold text-pink-700 mb-2">
                  Use Complementary Tools
                </h3>
                <p className="text-gray-700">
                  Pair this checker with ovulation kits or apps for multi-faceted tracking and more precise insights.
                </p>
              </div>
              <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
                <h3 className="text-lg font-semibold text-purple-700 mb-2">
                  Track Consistently
                </h3>
                <p className="text-gray-700">
                  Log data over several months to spot patterns and refine predictions for better conception planning.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MenstrualTracker;